// Activity entity registry — the single client-side source of truth for
// "what is this entity_type, where does its detail page live, what label
// should we show on its notifications?".
//
// Server-side, the permission resolver `_activity_can_read` (mig 058 +
// mig 062) carries a parallel CASE expression. Adding a new entity_type
// means touching BOTH: register here + add a CASE branch in the SQL.

import {processingRecordRoute} from './processingNav.js';

// ── the registry's own shapes ───────────────────────────────────────────────
//
// `ctx` is the entity's display context: the caller hands over whatever it
// already holds for that row (a batch, a cow, a daily report), and each entry
// reads the ONE field it labels with. The union below is the exhaustive list of
// fields the entries actually read — every key optional, because a caller with
// a cattle row has no `housing_name` and never should.
export type ActivityEntityContext = {
  title?: string | null;
  name?: string | null;
  housing_name?: string | null;
  tag?: string | null;
  batchName?: string | null;
  label?: string | null;
  date?: string | null;
  batch_label?: string | null;
  herd?: string | null;
  flock?: string | null;
  species?: string | null;
};

// `route: null` is a real member of this type, not an oversight: fuel bills have
// no per-record page, and fabricating one would land the operator on the wrong
// surface (see the EQUIPMENT_FUEL_BILL entry). Every consumer already tests
// `typeof meta.route === 'function'` before calling it.
// The builders in ACTIVITY_REGISTRY are left UNANNOTATED and take their
// parameter types from this declaration by contextual typing. That is not a
// style choice: a dozen static guards read those entries as literal source
// lines (`route: (id) => '/layer/batches/' + id`), so the registry's text has to
// stay byte-identical while still being checked.
export type ActivityEntityMeta = {
  displayLabel: (id: string, ctx?: ActivityEntityContext | null) => string;
  route: ((id: string) => string) | null;
  program: string | null;
};

// The notification row shape resolveNotificationRoute branches on. Every field
// is optional: `list_recent_notifications` attaches the activity_* columns only
// when the emitting event resolved, and comment_id / task_instance_id belong to
// specific notification types.
export type ActivityNotification = {
  type?: string | null;
  task_instance_id?: string | null;
  activity_entity_type?: string | null;
  activity_entity_id?: string | null;
  comment_id?: string | null;
};

export const ENTITY_TYPES = {
  TASK_INSTANCE: 'task.instance',
  TODO_ITEM: 'todo.item',
  BROILER_BATCH: 'broiler.batch',
  LAYER_BATCH: 'layer.batch',
  LAYER_HOUSING: 'layer.housing',
  CATTLE_ANIMAL: 'cattle.animal',
  SHEEP_ANIMAL: 'sheep.animal',
  EQUIPMENT_ITEM: 'equipment.item',
  EQUIPMENT_FUEL_BILL: 'equipment.fuel_bill',
  EQUIPMENT_FUEL_SUPPLY: 'equipment.fuel_supply',
  REPAIR_BILL: 'repair.bill',
  REPAIR_ASSET: 'repair.asset',
  PROCESSING_RECORD: 'processing.record',
  PROCESSING_CUT_SHEET_PREFERENCES: 'processing.cut_sheet_preferences',
  PIG_BATCH: 'pig.batch',
  PIG_BREEDER: 'pig.breeder',
  PIG_FARROWING: 'pig.farrowing',
  CATTLE_PROCESSING: 'cattle.processing',
  CATTLE_FORECAST: 'cattle.forecast',
  CATTLE_BREEDING: 'cattle.breeding',
  CATTLE_LOG: 'cattle.log',
  SHEEP_PROCESSING: 'sheep.processing',
  POULTRY_DAILY: 'poultry.daily',
  LAYER_DAILY: 'layer.daily',
  EGG_DAILY: 'egg.daily',
  PIG_DAILY: 'pig.daily',
  CATTLE_DAILY: 'cattle.daily',
  SHEEP_DAILY: 'sheep.daily',
  WEIGHIN_SESSION: 'weighin.session',
  PROJECT_JOB: 'project.job',
};

export const ACTIVITY_REGISTRY: Record<string, ActivityEntityMeta> = {
  [ENTITY_TYPES.TASK_INSTANCE]: {
    displayLabel: (id, ctx) => (ctx && ctx.title ? ctx.title : id),
    route: (id) => `/tasks/${encodeURIComponent(id)}`,
    program: null,
  },
  [ENTITY_TYPES.TODO_ITEM]: {
    displayLabel: (id, ctx) => (ctx && ctx.title ? ctx.title : 'To Do'),
    route: (id) => `/tasks/todo/${encodeURIComponent(id)}`,
    program: null,
  },
  [ENTITY_TYPES.BROILER_BATCH]: {
    displayLabel: (id, ctx) => (ctx && ctx.name ? ctx.name : id),
    route: (id) => '/broiler/batches/' + encodeURIComponent(id),
    program: 'broiler',
  },
  [ENTITY_TYPES.LAYER_BATCH]: {
    displayLabel: (id, ctx) => (ctx && ctx.name ? ctx.name : id),
    route: (id) => '/layer/batches/' + id,
    program: 'layer',
  },
  [ENTITY_TYPES.LAYER_HOUSING]: {
    displayLabel: (id, ctx) => (ctx && ctx.housing_name ? ctx.housing_name : id),
    route: (id) => '/layer/housings/' + id,
    program: 'layer',
  },
  [ENTITY_TYPES.CATTLE_ANIMAL]: {
    displayLabel: (id, ctx) => (ctx && ctx.tag ? ctx.tag : id),
    route: (id) => '/cattle/herds/' + id,
    program: 'cattle',
  },
  [ENTITY_TYPES.SHEEP_ANIMAL]: {
    displayLabel: (id, ctx) => (ctx && ctx.tag ? ctx.tag : id),
    route: (id) => '/sheep/flocks/' + id,
    program: 'sheep',
  },
  [ENTITY_TYPES.EQUIPMENT_ITEM]: {
    displayLabel: (id, ctx) => (ctx && ctx.name ? ctx.name : id),
    route: (id) => `/fleet/${id}`,
    program: null,
  },
  // Fuel bills are admin-only supplier invoices with no per-bill record page and
  // no stable deep-link (they live in a tab inside /admin). route is null on
  // purpose so global Activity Log rows display the invoice/supplier label but
  // stay inert — we do NOT fabricate a /fleet route that would land the operator
  // on the wrong surface. The label comes from the event's entity_label.
  [ENTITY_TYPES.EQUIPMENT_FUEL_BILL]: {
    displayLabel: (id, ctx) => (ctx && ctx.label ? ctx.label : id),
    route: null,
    program: null,
  },
  // Supply reports have no stable per-record route. Use the server's stored
  // date/destination label and keep the Activity row inert.
  [ENTITY_TYPES.EQUIPMENT_FUEL_SUPPLY]: {
    displayLabel: (id, ctx) => (ctx && ctx.label ? ctx.label : 'Fuel Supply'),
    route: null,
    program: null,
  },
  // Repair Bills (mig 257): admin-only bills, written only by
  // repair_bill_operation. A bill opens in its modal on the Admin Repair Bills
  // tab, addressed by &bill=<id> (N14). The label is the event's entity_label.
  [ENTITY_TYPES.REPAIR_BILL]: {
    displayLabel: (id, ctx) => (ctx && ctx.label ? ctx.label : 'Repair Bill'),
    route: (id) => '/admin?tab=repairBills&bill=' + encodeURIComponent(id),
    program: null,
  },
  // Repair items (mig 257): the freezers, vehicles and other items a bill is
  // split across. There is no per-item page, so it routes to the tab.
  [ENTITY_TYPES.REPAIR_ASSET]: {
    displayLabel: (id, ctx) => (ctx && ctx.label ? ctx.label : 'Repair Item'),
    route: () => '/admin?tab=repairBills',
    program: null,
  },
  // Processing Calendar records. Native Activity begins post-cutover. Routes
  // to the per-record deep link /processing?record=<id> (planner-integration
  // lane) — ProcessingCalendarView opens that record's drawer on load. A
  // missing id degrades to the flat /processing page.
  [ENTITY_TYPES.PROCESSING_RECORD]: {
    displayLabel: (id, ctx) => (ctx && ctx.title ? ctx.title : id),
    route: (id) => processingRecordRoute(id),
    program: null,
  },
  // Cut-sheet usual (migration 213): one shared set per species, entity_id is
  // the program. Rows are written only by update_processing_cut_sheet_preferences
  // (clients cannot post to it); the label comes from the event's entity_label
  // ('Pork usual', ...). There is no per-usual page, so it routes to /processing.
  [ENTITY_TYPES.PROCESSING_CUT_SHEET_PREFERENCES]: {
    displayLabel: (id, ctx) => (ctx && ctx.label ? ctx.label : 'Cut sheet usual'),
    route: () => '/processing',
    program: null,
  },
  [ENTITY_TYPES.PIG_BATCH]: {
    displayLabel: (id, ctx) => (ctx && ctx.batchName ? ctx.batchName : id),
    route: (id) => '/pig/batches/' + encodeURIComponent(id),
    program: 'pig',
  },
  [ENTITY_TYPES.PIG_BREEDER]: {
    displayLabel: (id, ctx) => (ctx && ctx.tag ? '#' + ctx.tag : id),
    route: (id) => '/pig/sows/' + encodeURIComponent(id),
    program: 'pig',
  },
  // Migration 235: farrowing records are written through pig_apply_operation,
  // which logs them on their own stream (one record page per farrowing).
  [ENTITY_TYPES.PIG_FARROWING]: {
    displayLabel: (id, ctx) => (ctx && ctx.label ? ctx.label : id),
    route: (id) => '/pig/farrowing/' + encodeURIComponent(id),
    program: 'pig',
  },
  [ENTITY_TYPES.CATTLE_PROCESSING]: {
    displayLabel: (id, ctx) => (ctx && ctx.name ? ctx.name : id),
    route: (id) => '/cattle/batches/' + id,
    program: 'cattle',
  },
  // Custom editable-table Activity: singleton cattle forecast workflow entity.
  // Month bucket logs filter this stream by payload.month_key; rows route to
  // the forecast view.
  [ENTITY_TYPES.CATTLE_FORECAST]: {
    displayLabel: () => 'Cattle Forecast',
    route: () => '/cattle/forecast',
    program: 'cattle',
  },
  // Custom editable-table Activity Phase B: singleton cattle breeding workflow.
  [ENTITY_TYPES.CATTLE_BREEDING]: {
    displayLabel: () => 'Cattle Breeding',
    route: () => '/cattle/breeding',
    program: 'cattle',
  },
  // Cattle Log: singleton field-log entity (entity_id 'cattle-log', migration
  // 112). Entries are comments; comment_mention notifications deep-link to
  // /cattle/log#comment-<id>. No per-entry record pages.
  [ENTITY_TYPES.CATTLE_LOG]: {
    displayLabel: () => 'Cattle Log',
    route: () => '/cattle/log',
    program: 'cattle',
  },
  [ENTITY_TYPES.SHEEP_PROCESSING]: {
    displayLabel: (id, ctx) => (ctx && ctx.name ? ctx.name : id),
    route: (id) => '/sheep/batches/' + id,
    program: 'sheep',
  },
  [ENTITY_TYPES.POULTRY_DAILY]: {
    displayLabel: (id, ctx) => (ctx && ctx.date ? ctx.date + (ctx.batch_label ? ' · ' + ctx.batch_label : '') : id),
    route: (id) => '/broiler/dailys/' + id,
    program: 'broiler',
  },
  [ENTITY_TYPES.LAYER_DAILY]: {
    displayLabel: (id, ctx) => (ctx && ctx.date ? ctx.date + (ctx.batch_label ? ' · ' + ctx.batch_label : '') : id),
    route: (id) => '/layer/dailys/' + id,
    program: 'layer',
  },
  [ENTITY_TYPES.EGG_DAILY]: {
    displayLabel: (id, ctx) => (ctx && ctx.date ? ctx.date : id),
    route: (id) => '/layer/eggs/' + id,
    program: 'layer',
  },
  [ENTITY_TYPES.PIG_DAILY]: {
    displayLabel: (id, ctx) => (ctx && ctx.date ? ctx.date + (ctx.batch_label ? ' · ' + ctx.batch_label : '') : id),
    route: (id) => '/pig/dailys/' + id,
    program: 'pig',
  },
  [ENTITY_TYPES.CATTLE_DAILY]: {
    displayLabel: (id, ctx) => (ctx && ctx.date ? ctx.date + (ctx.herd ? ' · ' + ctx.herd : '') : id),
    route: (id) => '/cattle/dailys/' + id,
    program: 'cattle',
  },
  [ENTITY_TYPES.SHEEP_DAILY]: {
    displayLabel: (id, ctx) => (ctx && ctx.date ? ctx.date + (ctx.flock ? ' · ' + ctx.flock : '') : id),
    route: (id) => '/sheep/dailys/' + id,
    program: 'sheep',
  },
  [ENTITY_TYPES.WEIGHIN_SESSION]: {
    displayLabel: (id, ctx) => (ctx && ctx.date ? ctx.date + (ctx.species ? ' · ' + ctx.species : '') : id),
    route: (id) => '/weigh-in-sessions/' + id,
    program: null,
  },
  // Projects (mig 256): one farm job. It opens in the Projects page drawer,
  // addressed by ?job=<id> (Design Constitution N14), not a record page.
  [ENTITY_TYPES.PROJECT_JOB]: {
    displayLabel: (id, ctx) => (ctx && ctx.title ? ctx.title : 'Job'),
    route: (id) => '/projects?job=' + encodeURIComponent(id),
    program: null,
  },
};

export function getActivityEntityMeta(entityType: string | null | undefined): ActivityEntityMeta | null {
  if (!entityType) return null;
  return ACTIVITY_REGISTRY[entityType] || null;
}

export function resolveNotificationRoute(
  notification: ActivityNotification | null | undefined,
  eventEntityType?: string | null,
  eventEntityId?: string | null,
): string {
  // To Do notifications. Approval/rejection/submitted route to the item's
  // record page via the activity-event entity columns the server attached;
  // todo_converted prefers the created Task when task_instance_id is set.
  if (
    notification &&
    (notification.type === 'todo_completion_approved' ||
      notification.type === 'todo_completion_rejected' ||
      notification.type === 'todo_completion_submitted' ||
      notification.type === 'todo_converted')
  ) {
    if (notification.type === 'todo_converted' && notification.task_instance_id) {
      return `/tasks/${encodeURIComponent(notification.task_instance_id)}`;
    }
    const et = notification.activity_entity_type || eventEntityType;
    const eid = notification.activity_entity_id || eventEntityId;
    if (et && eid) {
      const meta = getActivityEntityMeta(et);
      if (meta && typeof meta.route === 'function') {
        try {
          return meta.route(eid);
        } catch (_e) {
          /* fall through */
        }
      }
    }
    return '/tasks/todo';
  }
  // Processing work assignment (mig 177). The notification links the
  // processing.record Activity event the assignment emitted, so
  // list_recent_notifications attaches activity_entity_type='processing.record'
  // + activity_entity_id=<record id>; route through the registry to the
  // per-record deep link (/processing?record=<id>). Falls back to the flat
  // /processing page when event resolution is missing (event insert is
  // best-effort server-side).
  if (notification && notification.type === 'processing_subtask_assigned') {
    const et = notification.activity_entity_type || eventEntityType;
    const eid = notification.activity_entity_id || eventEntityId;
    if (et && eid) {
      const meta = getActivityEntityMeta(et);
      if (meta && typeof meta.route === 'function') {
        try {
          return meta.route(eid);
        } catch (_e) {
          /* fall through */
        }
      }
    }
    return '/processing';
  }
  if (notification && notification.type === 'comment_mention') {
    const et = notification.activity_entity_type;
    const eid = notification.activity_entity_id;
    if (et && eid) {
      const meta = getActivityEntityMeta(et);
      if (meta && typeof meta.route === 'function') {
        try {
          let route = meta.route(eid);
          if (notification.comment_id) route += '#comment-' + notification.comment_id;
          return route;
        } catch (_e) {
          /* fall through */
        }
      }
    }
  }
  // `eventEntityId != null` joins this condition: every other branch already
  // required an id before building a route, and a builder handed a missing id
  // produced a destination containing the literal 'null'/'undefined'. Falling
  // through is what an unroutable entity already does (see EQUIPMENT_FUEL_BILL).
  if (notification && notification.type === 'mention' && eventEntityType && eventEntityId != null) {
    const meta = getActivityEntityMeta(eventEntityType);
    if (meta && typeof meta.route === 'function') {
      try {
        return meta.route(eventEntityId);
      } catch (_e) {
        /* fall through */
      }
    }
  }
  if (notification && notification.task_instance_id) {
    return `/tasks/${encodeURIComponent(notification.task_instance_id)}`;
  }
  return '/tasks';
}

export function routeToView(routePath: string | null | undefined): {view: string; search: string} {
  if (!routePath) return {view: 'home', search: ''};
  const [path, search] = routePath.split('?');
  const VIEW_MAP = {
    '/tasks': 'tasks',
    '/processing': 'processing',
    '/broiler/batches': 'list',
    '/broiler/dailys': 'broilerdailys',
    '/layer': 'layersHome',
    '/layer/batches': 'layerbatches',
    '/layer/dailys': 'layerdailys',
    '/layer/eggs': 'eggdailys',
    '/pig/batches': 'pigbatches',
    '/pig/sows': 'sows',
    '/pig/dailys': 'pigdailys',
    '/cattle/batches': 'cattlebatches',
    '/cattle/herds': 'cattleherds',
    '/cattle/dailys': 'cattledailys',
    '/sheep/batches': 'sheepbatches',
    '/sheep/flocks': 'sheepflocks',
    '/sheep/dailys': 'sheepdailys',
    '/fleet': 'equipmentHome',
    '/admin': 'webforms',
  };
  // Declared separately so the literal above keeps the exact shape the
  // refactor-route oracle slices out of this file, while the lookup below is
  // still a checked string index.
  const viewByPath: Record<string, string> = VIEW_MAP;
  if (path.startsWith('/tasks/')) return {view: 'tasks', search: search || ''};
  if (path.startsWith('/fleet/')) return {view: 'equipmentHome', search: search || ''};
  if (path.startsWith('/cattle/herds/')) return {view: 'cattleherds', search: search || ''};
  if (path.startsWith('/cattle/batches/')) return {view: 'cattlebatches', search: search || ''};
  if (path.startsWith('/sheep/flocks/')) return {view: 'sheepflocks', search: search || ''};
  if (path.startsWith('/sheep/batches/')) return {view: 'sheepbatches', search: search || ''};
  if (path.startsWith('/broiler/dailys/')) return {view: 'broilerdailys', search: search || ''};
  if (path.startsWith('/pig/batches/')) return {view: 'pigbatches', search: search || ''};
  if (path.startsWith('/pig/sows/')) return {view: 'sows', search: search || ''};
  if (path.startsWith('/pig/dailys/')) return {view: 'pigdailys', search: search || ''};
  if (path.startsWith('/cattle/dailys/')) return {view: 'cattledailys', search: search || ''};
  if (path.startsWith('/sheep/dailys/')) return {view: 'sheepdailys', search: search || ''};
  if (path.startsWith('/layer/dailys/')) return {view: 'layerdailys', search: search || ''};
  if (path.startsWith('/layer/eggs/')) return {view: 'eggdailys', search: search || ''};
  if (path.startsWith('/layer/batches/')) return {view: 'layerbatches', search: search || ''};
  if (path.startsWith('/layer/housings/')) return {view: 'layerbatches', search: search || ''};
  if (path.startsWith('/broiler/batches/')) return {view: 'list', search: search || ''};
  if (path.startsWith('/weigh-in-sessions/')) return {view: 'weighinsessions', search: search || ''};
  return {view: viewByPath[path] || 'home', search: search || ''};
}
